import { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { products as productsApi } from '../lib/api';
import { ChevronLeft, ChevronRight, Sparkles, ShieldCheck, Tag, TrendingDown } from 'lucide-react';

/**
 * Amazon-style "Compare with similar items" horizontal rail.
 * Below a selected product, shows up to N similar listings from OTHER stores
 * with key specs and a quick price-delta badge.
 *
 * Props:
 *   productId – the source product to find similar for
 *   onClose   – optional callback when user dismisses the panel
 */
export default function SimilarItems({ productId, onClose }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(true);
  const railRef = useRef(null);

  useEffect(() => {
    let cancel = false;
    if (!productId) return;
    setBusy(true);
    productsApi.similar(productId, 10)
      .then(d => { if (!cancel) setData(d); })
      .catch(() => {})
      .finally(() => { if (!cancel) setBusy(false); });
    return () => { cancel = true; };
  }, [productId]);

  const scroll = (delta) => {
    if (!railRef.current) return;
    railRef.current.scrollBy({ left: delta, behavior: 'smooth' });
  };

  if (!productId) return null;

  return (
    <section data-testid="similar-items" className="mt-7 card-soft p-5 relative">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#D35400]">Smart suggestion</div>
          <h3 className="font-heading font-bold text-xl mt-1">Compare with similar items</h3>
          {data?.source && (
            <div className="text-xs text-ink/60 mt-0.5">Showing alternatives for <b className="text-ink">{data.source.name}</b> · @ {data.source.store_name} ₹{data.source.price}</div>
          )}
        </div>
        <div className="flex items-center gap-1">
          <button data-testid="similar-prev" onClick={() => scroll(-380)} aria-label="Scroll left"
            className="w-9 h-9 grid place-items-center rounded-full border border-border bg-white hover:bg-muted transition">
            <ChevronLeft className="w-4 h-4"/>
          </button>
          <button data-testid="similar-next" onClick={() => scroll(380)} aria-label="Scroll right"
            className="w-9 h-9 grid place-items-center rounded-full border border-border bg-white hover:bg-muted transition">
            <ChevronRight className="w-4 h-4"/>
          </button>
          {onClose && (
            <button onClick={onClose} className="ml-2 text-xs text-ink/50 hover:text-ink underline">Close</button>
          )}
        </div>
      </div>

      {busy && (
        <div className="mt-5 flex gap-3 overflow-hidden">
          {[1,2,3,4].map(i => (
            <div key={i} className="w-[280px] shrink-0 rounded-xl bg-muted animate-pulse h-[320px]"/>
          ))}
        </div>
      )}

      {!busy && (data?.results?.length || 0) === 0 && (
        <div className="mt-5 text-sm text-ink/50 text-center py-8">No similar items yet. Check back as more stores list this product.</div>
      )}

      {!busy && (data?.results?.length || 0) > 0 && (
        <>
          <div ref={railRef} className="mt-5 flex gap-3 overflow-x-auto pb-2 snap-x snap-mandatory scroll-pl-1 [scrollbar-width:thin]">
            {data.results.map(r => (
              <Link
                key={r.product_id}
                to={`/stores/${r.store_id}`}
                data-testid={`similar-card-${r.product_id}`}
                className="snap-start w-[280px] shrink-0 rounded-xl border border-border bg-white hover:shadow-xl hover:border-[#E67E22] transition overflow-hidden flex flex-col"
              >
                <div className="aspect-[5/3] bg-muted relative overflow-hidden">
                  {r.image_url && <img loading="lazy" src={r.image_url.startsWith('http') ? r.image_url : `${process.env.REACT_APP_BACKEND_URL}${r.image_url}`} alt={r.name} className="w-full h-full object-cover group-hover:scale-105 transition"/>}
                  {/* Best-price badge */}
                  {r.is_cheapest && (
                    <span className="absolute top-2 left-2 inline-flex items-center gap-1 rounded-full bg-[#1B5E20] text-white text-[10px] uppercase tracking-wider font-bold px-2 py-1 shadow-sm"><Sparkles className="w-3 h-3"/>Best price</span>
                  )}
                  {/* Trust + verified */}
                  {r.store_verified && (
                    <span className="absolute top-2 right-2 inline-flex items-center gap-1 rounded-full bg-white text-[#1B5E20] text-[10px] font-bold px-2 py-1 shadow-sm border border-[#A5D6A7]"><ShieldCheck className="w-3 h-3"/>Verified</span>
                  )}
                  {/* Out of stock */}
                  {!r.in_stock && (
                    <div className="absolute inset-0 bg-black/50 grid place-items-center">
                      <span className="text-white text-xs font-bold uppercase tracking-wider">Out of stock</span>
                    </div>
                  )}
                </div>
                <div className="p-3 flex-1 flex flex-col">
                  <div className="font-heading font-bold text-sm line-clamp-2 leading-tight text-ink">{r.name}</div>
                  <div className="text-[11px] text-ink/60 mt-0.5">{r.store_name} · {r.store_village}</div>

                  <div className="mt-2 flex items-baseline gap-2">
                    <span className="font-heading font-black text-xl text-ink">₹{r.price}</span>
                    <span className="text-[11px] text-ink/50">/ {r.unit}</span>
                    {r.delta_vs_cheapest > 0 && (
                      <span className="ml-auto inline-flex items-center gap-0.5 text-[10px] font-bold text-[#9A4500]"><TrendingDown className="w-2.5 h-2.5"/>+₹{r.delta_vs_cheapest}</span>
                    )}
                  </div>

                  {/* Quick comparison chips */}
                  <div className="mt-2 flex flex-wrap gap-1 text-[10px]">
                    {r.brand && <span className="rounded bg-[#FFF3E0] text-[#9A4500] px-1.5 py-0.5 font-semibold">{r.brand}</span>}
                    <span className={`rounded px-1.5 py-0.5 font-semibold uppercase ${r.quality_grade === 'premium' ? 'bg-[#FFF8E1] text-[#7A4A00]' : r.quality_grade === 'economy' ? 'bg-muted text-ink/70' : 'bg-[#E3F2FD] text-[#0D47A1]'}`}>{r.quality_grade}</span>
                    {r.returnable && <span className="rounded bg-[#E8F5E9] text-[#1B5E20] px-1.5 py-0.5 font-semibold">Returnable</span>}
                  </div>

                  {/* Bottom row: trust + delivery */}
                  <div className="mt-auto pt-3 flex items-center justify-between text-[10px] text-ink/60">
                    <span className="inline-flex items-center gap-1"><Tag className="w-2.5 h-2.5"/>Delivery ₹{r.delivery_fee}</span>
                    <span className="inline-flex items-center gap-1">Trust <b className={`${r.store_trust_score >= 80 ? 'text-[#1B5E20]' : r.store_trust_score >= 60 ? 'text-[#9A4500]' : 'text-ink/60'}`}>{r.store_trust_score}</b></span>
                  </div>
                </div>
              </Link>
            ))}
          </div>

          {/* Quick comparison table (collapsible-ish — just show key columns) */}
          <details data-testid="similar-table" className="mt-3 rounded-lg border border-border bg-[#FBF8F1] open:bg-white open:shadow-sm">
            <summary className="cursor-pointer select-none px-4 py-2 text-xs font-bold uppercase tracking-[0.16em] text-[#D35400] hover:text-[#B84300]">
              Quick comparison table ↓
            </summary>
            <div className="overflow-x-auto px-4 pb-3">
              <table className="w-full text-sm mt-2">
                <thead className="text-[10px] uppercase tracking-[0.16em] font-bold text-ink/50 text-left">
                  <tr>
                    <th className="py-2 pr-3">Item</th>
                    <th className="py-2 pr-3">Brand</th>
                    <th className="py-2 pr-3">Grade</th>
                    <th className="py-2 pr-3">Store</th>
                    <th className="py-2 pr-3">Trust</th>
                    <th className="py-2 pr-3">Delivery</th>
                    <th className="py-2 pr-3 text-right">Price</th>
                    <th className="py-2 pr-3 text-right">Δ</th>
                  </tr>
                </thead>
                <tbody>
                  {data.results.map(r => (
                    <tr key={r.product_id} className="border-t border-border">
                      <td className="py-2 pr-3 max-w-[200px] truncate">{r.name}</td>
                      <td className="py-2 pr-3">{r.brand || '—'}</td>
                      <td className="py-2 pr-3 capitalize">{r.quality_grade}</td>
                      <td className="py-2 pr-3">{r.store_name}</td>
                      <td className="py-2 pr-3 font-mono">{r.store_trust_score}</td>
                      <td className="py-2 pr-3">₹{r.delivery_fee}</td>
                      <td className="py-2 pr-3 text-right font-bold">₹{r.price}</td>
                      <td className={`py-2 pr-3 text-right text-xs ${r.is_cheapest ? 'text-[#1B5E20] font-bold' : 'text-[#9A4500]'}`}>
                        {r.is_cheapest ? 'cheapest' : `+₹${r.delta_vs_cheapest}`}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </details>
        </>
      )}
    </section>
  );
}
